import type { ModelPreset } from "./models.ts";
import type { Target } from "./reserve.ts";

export const GiB = 1024 ** 3;

// ── Cache KV ────────────────────────────────────────────────────────────────

/**
 * Un groupe de couches partageant la même géométrie de cache.
 *
 * Une seule formule `2 × couches × têtes_kv × head_dim` ne suffit plus en 2026 :
 * fenêtres glissantes (Gemma 4, gpt-oss), attention linéaire sans KV (Qwen 3.5+,
 * Nemotron, Kimi K3), latent compressé (MLA), compression le long de la séquence
 * (DeepSeek V4). Chaque modèle est donc décrit comme une somme de groupes.
 */
export type KvGroup = {
  layers: number;
  /** Éléments stockés par token et par couche : 2·têtes_kv·head_dim en GQA, 576 en MLA. */
  width: number;
  /** Fenêtre glissante : tokens gardés = min(contexte, fenêtre). */
  window?: number;
  /** Compression de séquence (DeepSeek V4) : tokens gardés = contexte / ratio. */
  ratio?: number;
  /**
   * Têtes KV que le parallélisme tensoriel répartit entre GPU (GQA). Absent : latent
   * unique, répliqué sur chaque GPU (MLA, DeepSeek V4) — c'est ce que font vLLM et
   * SGLang hors « DP attention ».
   */
  tpHeads?: number;
  /**
   * Octets par token gardé et par couche, quel que soit le format choisi pour le cache : clés
   * d'indexeur que vLLM stocke dans leur propre format (DSA de DeepSeek et GLM : 128 en FP8 + une
   * échelle de 4 octets = 132 ; QSA de Qwen : 128 en BF16 = 256).
   */
  fixedBytes?: number;
  /**
   * Octets par token et par couche quand le cache est en FP8 dans le format compact de vLLM
   * (FlashMLA « fp8_ds_mla ») : 656 pour le MLA clairsemé (512 en FP8 + 16 d'échelles + RoPE en
   * BF16), 584 pour DeepSeek V4 (448 + 128 + 8). Absent : largeur × 1 octet.
   */
  fp8Bytes?: number;
};

/** Octets d'un token d'un groupe de couches pour un format de cache (kvBytes : 2 en BF16, 1 en FP8). */
const tokenBytes = (g: KvGroup, kvBytes: number) => g.fixedBytes ?? (kvBytes === 1 && g.fp8Bytes ? g.fp8Bytes : g.width * kvBytes);

// Formats propres à vLLM, relevés dans son code (vllm/models/deepseek_v4/attention.py,
// v1/attention/backends/mla/flashmla_sparse.py, deepseek_v2.py : DeepseekV32IndexerCache).
/** Clés d'indexeur DSA : FP8 + une échelle FP32 par bloc de 128. */
export const DSA_INDEXER_BYTES = (dim: number) => dim + (dim / 128) * 4;
/** MLA clairsemé (DeepSeek V3.2, GLM-5) en FP8 : latent 512 en FP8, 4 échelles FP32, RoPE 64 en BF16. */
export const SPARSE_MLA_FP8_BYTES = 656;
/** DeepSeek V4 et V4.1 en FP8 (GPU Hopper ; 528 sur Blackwell pour V4.1) : 448 + 128 + 8. */
export const DSV4_FP8_BYTES = 584;

/** `vDim` : taille des valeurs quand elle diffère de celle des clés (MiMo-V2.6 : 192 et 128). */
export const gqa = (layers: number, kvHeads: number, headDim: number, window?: number, vDim = headDim): KvGroup => ({
  layers,
  width: kvHeads * (headDim + vDim),
  tpHeads: kvHeads,
  ...(window ? { window } : {}),
});

/** MLA (DeepSeek V3, GLM-5, Kimi) : un latent de kv_lora_rank + la clé RoPE, pas de K/V séparés. */
export const mla = (layers: number, kvLoraRank: number, ropeDim: number): KvGroup => ({
  layers,
  width: kvLoraRank + ropeDim,
});

// ── État récurrent ──────────────────────────────────────────────────────────
// Les couches à attention linéaire / Mamba ne cachent pas de KV : elles gardent un
// état de taille FIXE par séquence, quel que soit le contexte. Négligeable à une
// séquence, il pèse vite à forte concurrence (Kimi K3 : ~430 MiB par séquence).
// L'état SSM est en fp32 (mamba_ssm_dtype dans les configs), le conv en bf16.

/** Gated DeltaNet (Qwen 3.5 / 3.6 / 3.8). */
export const gdnState = (layers: number, kHeads: number, vHeads: number, kDim: number, vDim: number, kernel: number) =>
  layers * (vHeads * kDim * vDim * 4 + (2 * kHeads * kDim + vHeads * vDim) * (kernel - 1) * 2);

/** Mamba-2 (Nemotron 3). */
export const mamba2State = (layers: number, heads: number, headDim: number, stateSize: number, groups: number, kernel: number) =>
  layers * (heads * headDim * stateSize * 4 + (heads * headDim + 2 * groups * stateSize) * (kernel - 1) * 2);

/** Kimi Delta Attention (Kimi K3). */
export const kdaState = (layers: number, heads: number, headDim: number, kernel: number) =>
  layers * (heads * headDim * headDim * 4 + 3 * heads * headDim * (kernel - 1) * 2);

/** LFM2 (Liquid AI) : convolutions courtes, une fenêtre de `cache` tokens par canal, en bf16. */
export const convState = (layers: number, dim: number, cache: number) => layers * dim * cache * 2;

/**
 * DeepSeek V4 : états des compresseurs (inference/model.py, buffers kv_state et
 * score_state, fp32). Couche ratio 4 (fenêtres chevauchantes) : 64 Kio + 16 Kio
 * pour le compresseur de l'indexeur. Couche ratio 128 : 512 Kio.
 */
export const dsv4State = (ratio4Layers: number, ratio128Layers: number) =>
  ratio4Layers * (65536 + 16384) + ratio128Layers * 524288;

/** DeepSeek V4.1 (CSA2) : kv_state + score_state (fp32) des couches sources qui compressent. */
export const csa2State = (layers: number, ratio: number, headDim: number) => layers * 2 * ratio * headDim * 4;

// ── Calcul ──────────────────────────────────────────────────────────────────

export type Mode = "inference" | "full" | "lora" | "qlora";

export type Inputs = {
  model: ModelPreset;
  mode: Mode;
  /** Bits par poids ; 0 = checkpoint natif. */
  weightBits: number;
  /** Taille exacte des fichiers de poids quand on la connaît (version GGUF publiée sur Hugging
   *  Face, voir ggufFiles dans catalog.ts) : remplace l'estimation paramètres × bits. */
  exactWeightBytes?: number;
  /** llama.cpp (et Ollama, LM Studio) : les GPU se partagent les couches (-sm layer, pas de
   *  tensoriel) et l'embedding d'entrée reste en RAM. Voir splitFor et weightsPerGpu. */
  layerSplit?: boolean;
  kvBytes: number;
  context: number;
  /** Séquences simultanées (inférence) ou micro-batch par GPU (entraînement). */
  sequences: number;
  tp: number;
  pp: number;
  vramGiB: number;
  utilization: number;
  overheadGiB: number;
  /** Moteur et GPU visés : compute() prend alors la part utilisable et le surcoût que ce moteur règle par défaut
   *  sur ce GPU (reserve.ts), sauf réglage saisi, et l'arrondi des MoE FP4 de vLLM. Absent : utilization et
   *  overheadGiB tels quels. */
  target?: Target;
  // Entraînement
  gradientCheckpointing: boolean;
  shardStates: boolean;
  loraFraction: number;
};

/**
 * Tokens gardés en cache, par séquence, pour un groupe de couches. swaPad : marge des couches à
 * fenêtre glissante. llama.cpp garde la fenêtre plus un lot de calcul (n_swa + n_ubatch, 512 tokens
 * partagés entre les séquences, src/llama-kv-cache-iswa.cpp) ; vLLM, la fenêtre seule.
 */
export function cachedTokens(g: KvGroup, context: number, swaPad = 0): number {
  if (g.window) return Math.min(context, g.window + swaPad);
  if (g.ratio) return Math.ceil(context / g.ratio);
  return context;
}

/** Part d'un groupe gardée sur chaque GPU (GQA réparti par TP, latent répliqué). */
function tpFraction(g: KvGroup, tp: number): number {
  // Comme vLLM : max(1, têtes // tp) têtes par GPU, donc réplication si tp > têtes.
  return g.tpHeads ? Math.max(1, g.tpHeads / tp) / g.tpHeads : 1;
}

/** Octets de KV par séquence, sur un GPU donné. */
export function kvBytesPerSequence(model: ModelPreset, context: number, kvBytes: number, tp = 1, pp = 1, swaPad = 0): number {
  return model.kv.reduce(
    (sum, g) => sum + (g.layers / pp) * tokenBytes(g, kvBytes) * tpFraction(g, tp) * cachedTokens(g, context, swaPad),
    0,
  );
}

/** Lot de calcul de llama.cpp (n_ubatch par défaut), gardé en plus de la fenêtre glissante. */
export const LLAMACPP_UBATCH = 512;

/** Octets de KV par token à plein contexte, sur un seul GPU — la métrique à comparer entre modèles. */
export function kvBytesPerToken(model: ModelPreset, context: number, kvBytes: number): number {
  return context > 0 ? kvBytesPerSequence(model, context, kvBytes) / context : 0;
}
